import { Configuration, StoresApi } from '@/api';
import useGetStoreQuery from '@/api/queries/useGetStoreQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { isBillingEnabledFor } from '@/config';
import { Store, StoreAddressType, StoreAssociation, StoreCreate } from '@/models/Store';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import StoreBillingPanel from '@/modules/admin/components/StoreBillingPanel';
import StoreForm from '@/modules/admin/components/StoreForm';
import {
  AdminStoreListPath,
  RetailerStoreEditAddressPath,
  RetailerStoreEditPath,
  RetailerStoreListPath,
  StoreEditAddressPath,
  StoreEditPath,
} from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

type StoreEditTab = 'details' | 'business-address' | 'delivery-address' | 'billing';

const addressTabBySlug: Record<
  'business-address' | 'delivery-address',
  StoreAddressType.BUSINESS | StoreAddressType.DELIVERY
> = {
  'business-address': StoreAddressType.BUSINESS,
  'delivery-address': StoreAddressType.DELIVERY,
};

const allTabs: Array<{
  id: StoreEditTab;
  label: string;
  description: string;
}> = [
  {
    id: 'details',
    label: 'Details',
    description: 'Update the store profile, contact information, and operational settings.',
  },
  {
    id: 'business-address',
    label: 'Business Address',
    description: 'Primary legal and operational address for the store.',
  },
  {
    id: 'delivery-address',
    label: 'Delivery Address',
    description: 'Address used for deliveries and receiving operations.',
  },
  {
    id: 'billing',
    label: 'Billing',
    description: 'Manage subscription details and enable billing for this store.',
  },
];

const StoreEdit = () => {
  const navigate = useNavigate();
  const isBillingEnabled = isBillingEnabledFor('store');
  const { retailerId, storeId, address } = useParams();
  const tabs = isBillingEnabled ? allTabs : allTabs.filter((tab) => tab.id !== 'billing');
  const activeTab: StoreEditTab =
    address === 'business-address' ||
    address === 'delivery-address' ||
    (address === 'billing' && isBillingEnabled)
      ? address
      : 'details';
  const activeAddressType =
    activeTab === 'business-address' || activeTab === 'delivery-address'
      ? addressTabBySlug[activeTab]
      : StoreAddressType.BUSINESS;
  const selectedTab = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  const { isLoading, store, invalidateQuery } = useGetStoreQuery(storeId, undefined, [
    StoreAssociation.RETAILER,
  ]);

  const storesApi = new StoresApi(new Configuration(useAuth));

  function isStoreUpdate(s: StoreCreate | Store): s is Store {
    return 'id' in s;
  }

  const handleOnSubmit = async (store: StoreCreate | Store): Promise<void> => {
    if (!isStoreUpdate(store)) {
      return;
    }

    try {
      await storesApi.updateStore(store.id, store);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Store "${store.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
      navigate(
        retailerId ? RetailerStoreListPath.withRetailerId(store.retailerId) : AdminStoreListPath,
      );
    } catch (error) {
      console.error('Failed to update store:', error);
    }
  };

  const handleOnDelete = async () => {
    if (!store?.id) {
      return;
    }

    try {
      await storesApi.deleteStore(store.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Store "${store.name}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      navigate(
        retailerId ? RetailerStoreListPath.withRetailerId(store.retailerId) : AdminStoreListPath,
      );
    } catch (error) {
      console.error('Failed to delete store:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!store) {
    return;
  }

  const handleTabChange = (tabId: StoreEditTab) => {
    if (!storeId) {
      return;
    }

    if (tabId === 'details') {
      navigate(
        retailerId
          ? RetailerStoreEditPath.withParams(retailerId, storeId)
          : StoreEditPath.withId(storeId),
        { replace: true },
      );
      return;
    }

    navigate(
      retailerId
        ? RetailerStoreEditAddressPath.withParams(retailerId, storeId, tabId)
        : StoreEditAddressPath.withParams(storeId, tabId),
      { replace: true },
    );
  };

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Edit Store">
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete Store"
          message={`Are you sure you want to delete store "${store.name}"? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Store
            </Button>
          )}
        </Confirm>
      </PageHeader>

      <div className="w-full bg-white mx-auto rounded-lg shadow p-6 lg:p-12">
        <div className="mb-8 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="mt-3 px-1 text-sm text-gray-500">{selectedTab.description}</div>
        </div>

        {activeTab === 'billing' ? (
          <StoreBillingPanel store={store} />
        ) : activeTab === 'details' ? (
          <StoreForm onSubmit={handleOnSubmit} store={store} mode="details" />
        ) : (
          <StoreForm
            onSubmit={handleOnSubmit}
            store={store}
            activeAddressType={activeAddressType}
            mode="address"
          />
        )}
      </div>
    </>
  );
};

export default StoreEdit;
